import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { validateEmail } from '../utils/validation';
import { Mail, Lock, AlertCircle, Loader2, Send } from 'lucide-react';

export function AuthForm() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [isSignUp, setIsSignUp] = useState(false);
  const [isRecovering, setIsRecovering] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      if (isRecovering) {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reset-password`,
        });
        if (error) throw error;
        setMessage('Revisa tu correo para restablecer la contraseña');
      } else if (isSignUp) {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/auth/callback`,
            data: {
              email_confirmed_at: null,
            }
          },
        });
        if (error) throw error;
        setMessage('Te hemos enviado un correo de verificación. Por favor, revisa tu bandeja de entrada y sigue las instrucciones para activar tu cuenta.');
      } else {
        const { data: { user }, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        
        if (error) throw error;

        // Verificar si el usuario ha confirmado su correo
        if (user && !user.email_confirmed_at) {
          setError('Por favor, verifica tu correo electrónico antes de iniciar sesión');
          await supabase.auth.signOut();
          return;
        }
      }
    } catch (err) {
      console.error('Auth error:', err);
      setError(err instanceof Error ? err.message : 'Ha ocurrido un error');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email || !validateEmail(email)) {
      setError('Por favor, ingresa un correo electrónico válido');
      return;
    }

    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) throw error;
      setMessage('Correo de verificación reenviado. Por favor, revisa tu bandeja de entrada.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al reenviar el correo de verificación');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    if (!email) {
      setError('El correo electrónico es obligatorio');
      return false;
    }
    if (!validateEmail(email)) {
      setError('Por favor, ingresa un correo electrónico válido');
      return false;
    }
    if (!isRecovering && !password) {
      setError('La contraseña es obligatoria');
      return false;
    }
    return true;
  };

  const toggleMode = () => {
    setError(null);
    setMessage(null);
    setIsRecovering(false);
    setIsSignUp(!isSignUp);
  };

  const toggleRecovery = () => {
    setError(null);
    setMessage(null);
    setIsRecovering(!isRecovering);
    setIsSignUp(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-xl shadow-lg">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {isRecovering
              ? 'Recuperar contraseña'
              : isSignUp
              ? 'Crear cuenta'
              : 'Iniciar sesión'}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {isSignUp ? 'Crea tu cuenta para comenzar' : 'Bienvenido de nuevo'}
          </p>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-4 animate-fade-in">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
                {error.includes('verifica tu correo') && (
                  <button
                    onClick={handleResendVerification}
                    className="mt-2 text-sm font-medium text-red-700 hover:text-red-600"
                  >
                    <Send className="w-4 h-4 inline mr-1" />
                    Reenviar correo de verificación
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {message && (
          <div className="rounded-md bg-green-50 p-4 animate-fade-in">
            <p className="text-sm text-green-700">{message}</p>
          </div>
        )}

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div className="relative">
              <label htmlFor="email" className="sr-only">
                Correo electrónico
              </label>
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="appearance-none rounded-t-md relative block w-full px-10 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Correo electrónico"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            {!isRecovering && (
              <div className="relative">
                <label htmlFor="password" className="sr-only">
                  Contraseña
                </label>
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete={isSignUp ? 'new-password' : 'current-password'}
                  required
                  className="appearance-none rounded-b-md relative block w-full px-10 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                  placeholder="Contraseña"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            )}
          </div>

          {!isSignUp && !isRecovering && (
            <div className="flex items-center justify-end">
              <button
                type="button"
                onClick={toggleRecovery}
                className="text-sm font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none focus:underline transition duration-150 ease-in-out"
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-150 ease-in-out disabled:bg-indigo-400 disabled:cursor-not-allowed"
            >
              {loading ? (
                <Loader2 className="animate-spin h-5 w-5" />
              ) : isRecovering ? (
                'Enviar instrucciones'
              ) : isSignUp ? (
                'Crear cuenta'
              ) : (
                'Iniciar sesión'
              )}
            </button>
          </div>

          <div className="text-sm text-center">
            {isRecovering ? (
              <button
                type="button"
                onClick={toggleRecovery}
                className="font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none focus:underline transition duration-150 ease-in-out"
              >
                Volver al inicio de sesión
              </button>
            ) : (
              <button
                type="button"
                onClick={toggleMode}
                className="font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none focus:underline transition duration-150 ease-in-out"
              >
                {isSignUp
                  ? '¿Ya tienes una cuenta? Inicia sesión'
                  : '¿No tienes una cuenta? Regístrate'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}